/**
 * Générateur PDF/A-3 Factur-X Amélioré
 * Résout les problèmes de validation en utilisant pdf-lib avec métadonnées complètes
 *
 * Changements clés:
 * - Métadonnées XMP PDF/A-3 complètes et valides
 * - Embarquement XML avec AFRelationship correct
 * - Support complet de la norme EN 16931
 */

import {
  PDFDocument,
  PDFName,
  PDFString,
  PDFDict,
  PDFArray,
} from 'pdf-lib';
import { logger } from '../logger';

export interface EmbedFacturXOptions {
  xmlContent: string;
  filename?: string;
  description?: string;
  invoiceNumber: string;
  seller: string;
  creationDate?: Date;
}

/**
 * Génère les métadonnées XMP complètes pour PDF/A-3
 */
function generatePDFA3Metadata(options: EmbedFacturXOptions): string {
  const now = options.creationDate || new Date();
  const xmpDate = now.toISOString();

  return `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
        xmlns:pdf="http://ns.adobe.com/pdf/1.3/"
        xmlns:xmp="http://ns.adobe.com/xap/1.0/"
        xmlns:dc="http://purl.org/dc/elements/1.1/"
        xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/"
        xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/"
        xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#"
        xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#"
        xmlns:fx="urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#">

      <!-- PDF/A-3 Identification -->
      <pdfaid:part>3</pdfaid:part>
      <pdfaid:conformance>B</pdfaid:conformance>

      <!-- Document Info -->
      <dc:format>application/pdf</dc:format>
      <dc:title>
        <rdf:Alt>
          <rdf:li xml:lang="fr-FR">Facture ${escapeXml(options.invoiceNumber)}</rdf:li>
        </rdf:Alt>
      </dc:title>
      <dc:creator>
        <rdf:Seq>
          <rdf:li>${escapeXml(options.seller)}</rdf:li>
        </rdf:Seq>
      </dc:creator>
      <dc:description>
        <rdf:Alt>
          <rdf:li xml:lang="fr-FR">Facture électronique Factur-X conforme EN 16931</rdf:li>
        </rdf:Alt>
      </dc:description>

      <!-- XMP Metadata -->
      <xmp:CreatorTool>FacturXPro - Factur-X Generator</xmp:CreatorTool>
      <xmp:CreateDate>${xmpDate}</xmp:CreateDate>
      <xmp:ModifyDate>${xmpDate}</xmp:ModifyDate>
      <xmp:MetadataDate>${xmpDate}</xmp:MetadataDate>

      <!-- PDF Metadata -->
      <pdf:Producer>FacturXPro with pdf-lib</pdf:Producer>

      <!-- Factur-X Extension Schema -->
      <pdfaExtension:schemas>
        <rdf:Bag>
          <rdf:li rdf:parseType="Resource">
            <pdfaSchema:schema>Factur-X PDFA Extension Schema</pdfaSchema:schema>
            <pdfaSchema:namespaceURI>urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#</pdfaSchema:namespaceURI>
            <pdfaSchema:prefix>fx</pdfaSchema:prefix>
            <pdfaSchema:property>
              <rdf:Seq>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentFileName</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>name of the embedded XML invoice file</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentType</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>INVOICE</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>Version</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>The actual version of the Factur-X XML schema</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>ConformanceLevel</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>The conformance level of the embedded Factur-X data</pdfaProperty:description>
                </rdf:li>
              </rdf:Seq>
            </pdfaSchema:property>
          </rdf:li>
        </rdf:Bag>
      </pdfaExtension:schemas>

      <!-- Factur-X Metadata -->
      <fx:DocumentFileName>factur-x.xml</fx:DocumentFileName>
      <fx:DocumentType>INVOICE</fx:DocumentType>
      <fx:Version>1.0</fx:Version>
      <fx:ConformanceLevel>EN 16931</fx:ConformanceLevel>

    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

/**
 * Échappe les caractères spéciaux XML
 */
function escapeXml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Formate une date au format PDF (D:YYYYMMDDHHmmssZ)
 */
function formatPdfDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `D:${year}${month}${day}${hours}${minutes}${seconds}Z`;
}

/**
 * Embarque le XML Factur-X dans un PDF avec pdf-lib
 * Version améliorée avec métadonnées complètes PDF/A-3
 */
export async function embedFacturXInPDF(
  pdfBytes: ArrayBuffer | Uint8Array,
  options: EmbedFacturXOptions
): Promise<Uint8Array> {

  const {
    xmlContent,
    filename = 'factur-x.xml',
    description = 'Facture Factur-X (EN 16931)',
    creationDate = new Date()
  } = options;

  // 1. Charger le PDF existant
  const pdfDoc = await PDFDocument.load(pdfBytes, {
    updateMetadata: false // On va gérer les métadonnées manuellement
  });

  // 2. Convertir le XML en UTF-8 bytes
  const xmlBytes = new TextEncoder().encode(xmlContent);

  // 3. Embarquer le fichier XML avec pdf-lib
  // Utilise l'API native qui gère correctement les métadonnées
  await pdfDoc.attach(xmlBytes, filename, {
    mimeType: 'text/xml',
    description: description,
    creationDate: creationDate,
    modificationDate: creationDate,
  });

  // 4. Sauvegarder une première fois pour finaliser la structure
  const intermediatePdfBytes = await pdfDoc.save({
    useObjectStreams: false,
    addDefaultPage: false,
    updateFieldAppearances: false,
  });

  // 5. Recharger le PDF pour accéder à la structure finalisée
  const pdfDoc2 = await PDFDocument.load(intermediatePdfBytes, {
    updateMetadata: false
  });

  // 6. Ajouter les métadonnées XMP PDF/A-3 personnalisées
  const xmpMetadata = generatePDFA3Metadata(options);
  const xmpBytes = new TextEncoder().encode(xmpMetadata);

  // Créer un stream XMP
  const xmpStream = pdfDoc2.context.stream(xmpBytes, {
    Type: 'Metadata',
    Subtype: 'XML',
  });

  const xmpStreamRef = pdfDoc2.context.register(xmpStream);

  // 7. Ajouter les métadonnées au catalogue du PDF
  const catalog = pdfDoc2.catalog;
  catalog.set(PDFName.of('Metadata'), xmpStreamRef);

  // 7b. Ajouter OutputIntent avec profil ICC sRGB pour conformité PDF/A-3
  // Ceci résout les erreurs DeviceRGB et DeviceGray
  try {
    // Charger le profil ICC sRGB depuis le dossier public
    const iccProfileResponse = await fetch('/sRGB2014.icc');
    const iccProfileBytes = new Uint8Array(await iccProfileResponse.arrayBuffer());

    // Créer un stream pour le profil ICC
    const iccStream = pdfDoc2.context.stream(iccProfileBytes);
    const iccStreamRef = pdfDoc2.context.register(iccStream);

    // Créer l'OutputIntent avec le profil ICC embarqué
    const outputIntentDict = pdfDoc2.context.obj({
      Type: 'OutputIntent',
      S: 'GTS_PDFA1',
      OutputConditionIdentifier: PDFString.of('sRGB IEC61966-2.1'),
      RegistryName: PDFString.of('http://www.color.org'),
      Info: PDFString.of('sRGB IEC61966-2.1'),
      DestOutputProfile: iccStreamRef,
    });

    const outputIntents = pdfDoc2.context.obj([outputIntentDict]);
    catalog.set(PDFName.of('OutputIntents'), outputIntents);
  } catch (error) {
    logger.error('Erreur lors de l\'ajout de OutputIntent:', error);
  }

  // 8. Marquer les pièces jointes avec AFRelationship = Data
  // C'est crucial pour Factur-X
  try {
    // Vérifier si le dictionnaire Names existe
    const namesEntry = catalog.get(PDFName.of('Names'));

    if (namesEntry) {
      const names = pdfDoc2.context.lookup(namesEntry);

      if (names instanceof PDFDict) {
        const embeddedFilesEntry = names.get(PDFName.of('EmbeddedFiles'));

        if (embeddedFilesEntry) {
          const embeddedFiles = pdfDoc2.context.lookup(embeddedFilesEntry);

          if (embeddedFiles instanceof PDFDict) {
            const namesArrayEntry = embeddedFiles.get(PDFName.of('Names'));

            if (namesArrayEntry) {
              const namesArray = pdfDoc2.context.lookup(namesArrayEntry);

              if (namesArray instanceof PDFArray && namesArray.size() > 1) {
                // Parcourir les paires [nom, filespec]
                for (let i = 1; i < namesArray.size(); i += 2) {
                  const fileSpecEntry = namesArray.get(i);

                  if (fileSpecEntry) {
                    const fileSpec = pdfDoc2.context.lookup(fileSpecEntry);

                    if (fileSpec instanceof PDFDict) {
                      // Ajouter AFRelationship (requis pour Factur-X EN 16931)
                      fileSpec.set(PDFName.of('AFRelationship'), PDFName.of('Data'));

                      // S'assurer que le fichier est marqué comme visible
                      fileSpec.set(PDFName.of('UF'), PDFString.of(filename));
                      fileSpec.set(PDFName.of('F'), PDFString.of(filename));
                      fileSpec.set(PDFName.of('Desc'), PDFString.of(description));
                    }
                  }
                }

                // Ajouter une référence AF dans le catalogue (requis pour PDF/A-3)
                const fileSpecRef = namesArray.get(1);
                if (fileSpecRef) {
                  const afArray = pdfDoc2.context.obj([fileSpecRef]);
                  catalog.set(PDFName.of('AF'), afArray);
                }
              }
            }
          }
        }
      }
    }
  } catch (error) {
    logger.error('Erreur lors de l\'ajout de AFRelationship:', error);
    // Ne pas faire échouer toute la génération si cette partie échoue
  }

  // 9. Définir les propriétés du document
  pdfDoc2.setTitle(`Facture ${options.invoiceNumber}`);
  pdfDoc2.setAuthor(options.seller);
  pdfDoc2.setSubject('Facture électronique Factur-X conforme EN 16931');
  pdfDoc2.setKeywords(['facture', 'invoice', 'factur-x', 'en16931', 'pdf/a-3']);
  pdfDoc2.setProducer('FacturXPro with pdf-lib');
  pdfDoc2.setCreator('FacturXPro - Factur-X Generator');
  pdfDoc2.setCreationDate(creationDate);
  pdfDoc2.setModificationDate(creationDate);

  // 10. Sauvegarder le PDF final avec les métadonnées
  const modifiedPdfBytes = await pdfDoc2.save({
    useObjectStreams: false, // Désactiver pour compatibilité PDF/A
    addDefaultPage: false,
    updateFieldAppearances: false,
  });

  return modifiedPdfBytes;
}

/**
 * Version simplifiée pour utilisation directe
 */
export async function createFacturXPdfImproved(
  pdfBytes: ArrayBuffer | Uint8Array,
  xmlContent: string,
  invoiceNumber: string,
  sellerName: string
): Promise<Uint8Array> {
  return embedFacturXInPDF(pdfBytes, {
    xmlContent,
    filename: 'factur-x.xml',
    description: `Facture Factur-X ${invoiceNumber} conforme EN 16931`,
    invoiceNumber,
    seller: sellerName,
    creationDate: new Date(),
  });
}
